import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  Monitor, 
  Layers, 
  AlertCircle,
  ChevronDown,
  Hash,
  Clock,
  ArrowLeft,
  Users,
  MoreHorizontal,
  LogOut
} from 'lucide-react';
import lineAPI from "../axios/lineAPI";
import ticketAPI from "../axios/ticketAPI";
import serviceApi from "../axios/serviceApi";
import { toast } from "react-toastify";

// --- LINE CARD COMPONENT (Giữ nguyên) ---
const LineCard = ({ line, onClick }) => {
  const { name, stats, currentTicket, latestTickets } = line;
  const waitingPreview = latestTickets ? latestTickets.slice(0, 3) : [];

  return (
    <div 
      className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col h-full overflow-hidden"
      onClick={onClick}
    >
      {/* 1. Header */}
      <div className="p-4 pb-2 flex justify-between items-start">
        <div className="flex items-center gap-3 overflow-hidden">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
              currentTicket ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'
          }`}>
            <Monitor size={20} />
          </div>
          <div className="min-w-0">
              <h3 className="font-bold text-slate-800 text-base truncate" title={name}>{name}</h3>
              <div className={`text-[11px] font-bold uppercase tracking-wider flex items-center gap-1 ${currentTicket ? 'text-green-600' : 'text-slate-400'}`}>
                  {currentTicket ? <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"/> : null}
                  {currentTicket ? 'Active' : 'Idle'}
              </div>
          </div>
        </div>
      </div>

      {/* 2. Body: Current Serving */}
      <div className="px-4 py-2">
        <div className="bg-slate-50 rounded-lg border border-slate-100 p-4 flex flex-col items-center justify-center min-h-[100px]">
            {currentTicket ? (
            <>
                <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest mb-1">Now Serving</span>
                <span className="text-4xl font-black text-slate-900 tracking-tighter tabular-nums mb-1">
                    {currentTicket.queueNumber}
                </span>
                <span className="text-xs text-slate-500 font-medium truncate max-w-full">
                    {currentTicket.name}
                </span>
            </>
            ) : (
            <div className="flex flex-col items-center opacity-40">
                <Layers size={28} className="text-slate-400 mb-1" />
                <span className="text-xs font-semibold text-slate-500">Counter Ready</span>
            </div>
            )}
        </div>
      </div>

      {/* 3. Waiting List Preview */}
      <div className="px-4 py-2 flex-1">
        <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-slate-500 flex items-center gap-1">
                <Users size={12}/> Queue ({stats.waiting})
            </span>
        </div>
        <div className="space-y-1.5">
            {waitingPreview.length > 0 ? (
                <>
                    {waitingPreview.map((ticket, idx) => (
                        <div key={ticket.id} className="flex justify-between items-center text-xs bg-white border border-slate-100 rounded px-2 py-1.5">
                            <div className="flex items-center gap-2 overflow-hidden">
                                <span className="w-4 h-4 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-[9px] font-bold">
                                    {idx + 1}
                                </span>
                                <span className="font-bold text-slate-700">{ticket.queueNumber}</span>
                            </div>
                            <span className="text-slate-400 truncate max-w-[80px]">{ticket.name}</span>
                        </div>
                    ))}
                    {stats.waiting > 3 && (
                        <div className="text-center text-[10px] text-slate-400 font-medium flex items-center justify-center gap-1">
                            <MoreHorizontal size={12}/> +{stats.waiting - 3} others
                        </div>
                    )}
                </>
            ) : (
                <div className="text-center py-2 border border-dashed border-slate-200 rounded text-xs text-slate-400">
                    No waiting tickets
                </div>
            )}
        </div>
      </div>

      {/* 4. Footer */}
      <div className="bg-slate-50 px-4 py-2 border-t border-slate-100 flex justify-between items-center mt-2">
        <div className="text-[10px] text-slate-400 font-medium">
            Total served: {stats.total - stats.waiting}
        </div>
        <div className="text-indigo-600 text-xs font-bold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
           Open <Hash size={12} />
        </div>
      </div>
    </div>
  );
};

// --- MAIN MONITOR PAGE ---
const MonitorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [services, setServices] = useState([]);
  const [activeService, setActiveService] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);

  // Load Services & Restore State
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await serviceApi.getService();
        let list = [];
        if (res.data && Array.isArray(res.data.data)) list = res.data.data;
        else if (res.data?.data?.services) list = res.data.data.services;
        else if (Array.isArray(res.data)) list = res.data;

        setServices(list);
        
        if (list.length > 0) {
            const returnedId = location.state?.returnServiceId;
            const savedId = sessionStorage.getItem("monitor_last_service_id");
            let targetId = returnedId || (savedId ? parseInt(savedId) : list[0].id);
            const exists = list.find(s => s.id === targetId);
            setActiveService(exists ? targetId : list[0].id);
        }
      } catch (err) {
        toast.error("Failed to load services");
      }
    };
    fetchServices();
  }, []);

  // Fetch Lines Data
  const fetchData = useCallback(async () => {
    if (!activeService) return;
    try {
      const resLines = await lineAPI.getLine(activeService);
      let rawLines = [];
      if (resLines.data?.data) {
        rawLines = Array.isArray(resLines.data.data) ? resLines.data.data : resLines.data.data.lines || [];
      }

      const fullData = await Promise.all(rawLines.map(async (line) => {
        try {
          const resTickets = await ticketAPI.getAll(line.id, { limit: 50 });
          let all = [];
          if (resTickets.data?.data) {
            if (Array.isArray(resTickets.data.data)) all = resTickets.data.data;
            else if (resTickets.data.data.tickets) all = resTickets.data.data.tickets;
            else if (resTickets.data.data.id) all = [resTickets.data.data];
          }

          all = all.map(t => ({
             ...t,
             queueNumber: t.queueNumber || t.queue_number || `#${t.id}`,
             name: t.name || t.user?.name || "Guest",
             status: (t.status || "waiting").toLowerCase()
          }));

          const serving = all.find(t => ['serving', 'processing'].includes(t.status));
          const waiting = all.filter(t => ['waiting', 'pending'].includes(t.status));

          return {
            id: line.id,
            name: line.name,
            stats: {
              total: (serving ? 1 : 0) + waiting.length,
              waiting: waiting.length,
              serving: serving ? 1 : 0
            },
            currentTicket: serving || null,
            latestTickets: waiting
          };
        } catch (e) {
          return { id: line.id, name: line.name, stats: { total: 0, waiting: 0 }, currentTicket: null, latestTickets: [] };
        }
      }));
      setLinesData(fullData);
    } catch (err) {
      console.error("Error fetching monitor data:", err);
    }
  }, [activeService]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleLineClick = (lineId) => {
    const selectedLine = linesData.find(l => l.id === lineId);
    if (!selectedLine) {
      toast.error("Counter information not found");
      return;
    }
    const lineInfo = { id: selectedLine.id, name: selectedLine.name, stats: selectedLine.stats };
    sessionStorage.setItem(`line_${lineId}`, JSON.stringify(lineInfo));
    
    navigate(`/counter?lineId=${lineId}`, { 
        state: { 
            lineInfo, 
            returnServiceId: activeService
        } 
    });
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate("/login");
  };

  const handleBack = () => {
    navigate("/dashboard"); 
  };

  const handleServiceChange = (serviceId) => {
    setActiveService(serviceId);
    setLinesData([]);
    setShowServiceDropdown(false);
    sessionStorage.setItem("monitor_last_service_id", serviceId);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showServiceDropdown && !e.target.closest('.service-dropdown-container')) {
        setShowServiceDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showServiceDropdown]);

  const currentUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const isAdmin = currentUser.role?.toLowerCase().includes("admin"); 
  const currentServiceName = services.find(s => s.id === activeService)?.name || "Select Service";

  return (
    <div className="flex-1 bg-gray-50 overflow-y-auto p-4 md:p-6 font-sans text-gray-800 h-screen flex flex-col">
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 shrink-0">
        <div className="flex items-center gap-3">
          {isAdmin && (
            <button
              onClick={handleBack}
              className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-indigo-600 transition-colors shadow-sm group"
              title="Back to Dashboard"
            >
              <ArrowLeft size={20} className="group-hover:-translate-x-0.5 transition-transform" />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Monitor className="text-indigo-600" size={24} /> Monitor System
            </h1>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto items-center">
            <div className="relative service-dropdown-container w-full sm:w-64">
                <button
                onClick={() => setShowServiceDropdown(!showServiceDropdown)}
                className="w-full flex items-center justify-between bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-xl font-medium shadow-sm hover:border-indigo-300 transition-all outline-none"
                >
                <div className="flex items-center gap-2 truncate">
                    <Layers size={18} className="text-indigo-500" />
                    <span className="truncate text-sm">{currentServiceName}</span>
                </div>
                <ChevronDown size={16} className={`text-gray-400 transition-transform ${showServiceDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showServiceDropdown && (
                <div className="absolute top-full left-0 mt-2 w-full bg-white border border-gray-100 rounded-xl shadow-xl z-50 overflow-hidden animate-fade-in-up">
                    <div className="max-h-[250px] overflow-y-auto py-1">
                    {services.map((svc) => (
                        <button
                        key={svc.id}
                        onClick={() => handleServiceChange(svc.id)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                            activeService === svc.id ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                        }`}
                        >
                        {activeService === svc.id && <div className="w-1.5 h-1.5 rounded-full bg-indigo-600"></div>}
                        <span className="truncate">{svc.name}</span>
                        </button>
                    ))}
                    </div>
                </div>
                )}
            </div>

            <div className="flex items-center gap-2 pl-0 sm:pl-3 sm:border-l border-gray-200">
                <div className="hidden md:flex flex-col items-end mr-2">
                    <span className="text-sm font-semibold text-gray-800 leading-tight">{currentUser.name || "User"}</span>
                    <span className="text-xs text-gray-500">{currentUser.role || "Staff"}</span>
                </div>
                
                {/* ẨN NÚT LOGOUT NẾU LÀ ADMIN */}
                {!isAdmin && (
                    <button 
                    onClick={handleLogout} 
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 shadow-sm transition-all"
                    title="Logout"
                    >
                    <LogOut size={18} />
                    </button>
                )}
            </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-y-auto">
        {!activeService ? (
           <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-dashed border-gray-300 text-center h-full max-h-[400px]">
                <div className="bg-indigo-50 p-4 rounded-full mb-4">
                  <AlertCircle size={40} className="text-indigo-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">No Service Selected</h3>
                <p className="text-gray-500 mt-2 max-w-xs">Please select a service from the dropdown above to view monitoring lines.</p>
          </div>
        ) : linesData.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pb-4">
            {linesData.map(line => (
              <LineCard
                key={line.id}
                line={line}
                onClick={() => handleLineClick(line.id)}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-dashed border-gray-300 text-center">
            <div className="bg-indigo-50 p-4 rounded-full mb-4">
              <Layers size={40} className="text-indigo-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">No Lines Found</h3>
            <p className="text-gray-500 mt-2 max-w-xs">There are no lines configured for this service yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonitorPage;